// AISEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'


const AISEOPage = () => {
    useEffect(() => {
        window.scroll(0, 0)
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const aiStats = [
        { number: '40%', label: 'of users now use AI search tools for research' },
        { number: '70%', label: 'higher engagement from AI-recommended content' },
        { number: '3x', label: 'more brand authority with structured AI optimization' },
        { number: '85%', label: 'of businesses see improved visibility with AI SEO' }
    ];

    const aiApproaches = [
        {
            icon: 'fas fa-search-plus',
            title: 'AI Search Strategy & Discovery',
            desc: 'We start with a deep analysis of how your audience searches using AI tools and how your brand currently appears. We focus on search intent in conversational and AI environments, entity relevance, topical authority, and content gaps to build a tailored AI SEO roadmap aligned with your business goals.'
        },
        {
            icon: 'fas fa-sitemap',
            title: 'AI-Friendly Site & Content Structure',
            desc: 'We optimize your site to make it easy for AI to read, understand, and trust. This includes semantic content architecture, structured data & schema markup, clear topical clustering, internal linking, and performance optimization.'
        },
        {
            icon: 'fas fa-brain',
            title: 'AI-Optimized Content Creation',
            desc: 'We create and refine content specifically designed for AI search experiences. This includes long-form authoritative articles, AI-friendly FAQs, comparison content, and natural language optimization for conversational queries.'
        },
        {
            icon: 'fas fa-shield-alt',
            title: 'Brand Authority & Trust Signals',
            desc: 'We strengthen your brand authority through consistent expertise signals, topical depth, brand mentions, and clear E-E-A-T optimization to ensure AI systems recognize you as a trusted source.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Measurement & Continuous Optimization',
            desc: 'AI search evolves fast. We continuously monitor visibility across AI platforms, content performance, emerging trends, and new optimization opportunities to keep your strategy ahead of the curve.'
        }
    ];

    const benefits = [
        ['Increased AI Visibility', 'Your brand appears in AI-generated answers across ChatGPT, Google SGE, and Gemini'],
        ['Higher Quality Traffic', 'Users arrive with stronger intent and engagement from AI-recommended content'],
        ['Stronger Brand Authority', 'AI recognizes and cites your brand as a trusted expert source'],
        ['Future-Proof Strategy', 'Your SEO stays ahead as search behavior shifts toward AI discovery']
    ];

    const targetBusinesses = [
        'SaaS & tech companies seeking innovation leadership',
        'E-commerce brands in competitive markets',
        'Professional services & consultancies',
        'Businesses investing in long-term organic growth',
        'Brands in high-competition digital landscapes'
    ];

    const aiQuestions = [
        'How does your brand appear in AI-generated search results?',
        'Is your content structured for AI systems to understand and reference?',
        'What authority signals does AI recognize about your brand?',
        'Are you visible in ChatGPT, Google SGE, and other AI platforms?',
        'How can you optimize for conversational and voice search queries?'
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">AI SEO</span> Services
                    </h1>
                    <p>Optimize your brand for ChatGPT, Google SGE, and AI-driven discovery</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    A <span className="sv-keyword-highlight">AI SEO strategy</span> ensures your business is visible, trusted, and recommended in AI-powered search platforms like ChatGPT, Google SGE, Gemini and other conversational tools.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>AI SEO services</strong> are designed to ensure your business is visible, trusted, and recommended in both classic search results and AI-generated answers. AI-driven search engines don't just rank pages — they select, summarize, and recommend information.
                                </p>
                                <p className="sv-description">
                                    This means ranking alone is no longer enough. Content must be understandable, authoritative, and structured for AI systems. Our comprehensive <strong>AI search optimization</strong> helps you master this new frontier of discovery.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        color='dark'
                                        tag={Link}
                                        to="/contact"

                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start AI SEO Strategy
                                    </Button>
                                    <Button
                                        color='dark'
                                        tag={Link}
                                        to="/contact"

                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">AI SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {aiStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is AI SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>AI SEO</strong> is the practice of optimizing your digital presence for AI-powered search platforms. Unlike traditional SEO focused on keyword rankings, AI SEO ensures your content is understandable, authoritative, and structured for AI systems to reference and recommend.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our AI SEO Services Answer:</h4>
                                    {aiQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in AI SEO?</h2>
                                <p className="sv-section-description">
                                    AI search engines analyze meaning, context, and authority before delivering answers. Your visibility is measured not just by where you rank, but by whether AI platforms choose your content to inform their responses.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-brain"></i>
                                        <h5>AI Visibility</h5>
                                        <p>Appear in AI-generated answers across ChatGPT, Google SGE, and Gemini</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Quality Traffic</h5>
                                        <p>Users arrive with stronger intent from AI-recommended content</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Brand Authority</h5>
                                        <p>AI recognizes your brand as a trusted expert source</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-rocket"></i>
                                        <h5>Future-Proof Strategy</h5>
                                        <p>Stay ahead as search behavior shifts toward AI discovery</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our AI SEO Approach at Reachtix</h2>
                                <p className="sv-section-description">
                                    We combine AI search strategy, technical implementation, and intelligent content design to ensure your brand is effectively optimized for next-generation search.
                                </p>

                                <div className="sv-approaches-grid">
                                    {aiApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of AI SEO With Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in AI SEO?</h2>
                                <p className="sv-section-description">
                                    Our <strong>AI SEO services</strong> deliver maximum value for businesses seeking leadership in digital visibility:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    By integrating AI SEO into your strategy, you move from being discovered to being recommended — mastering the next frontier of search visibility.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Lead the AI Search Revolution?</h2>
                                <p className="sv-cta-description">
                                    AI search is not the future — it's already here. If you want your brand to be recommended, referenced, and trusted by AI systems, Reachtix's AI SEO services give you the edge.
                                </p>
                                <p className="sv-cta-description">
                                    Let's build your AI search visibility and future-proof your organic growth strategy.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-brain mr-2"></i>
                                        Start AI SEO Journey
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get AI SEO Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AISEOPage;